import { Formik, FormikHelpers } from "formik";
import { FC, useMemo } from "react";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import yup from "app/util/yup";
import { useReportScopeQuery, useUpdateReportScopeMutation } from "app/api/graph/types";
import { reportScopeGraphId } from "app/api/graph/helpers";
import { RichTextEditor } from "app/component/form/text-editor/RichTextEditor";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { handleError, handleSuccess } from "app/api/graph/form";
import { StatusAlert } from "app/component/form/StatusAlert";

type Props = {
    scopeId: number;
    onUpdate?: () => void;
};

type FormValues = {
    inScope: string;
    outOfScope: string;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    inScope: yup.string().required(),
    outOfScope: yup.string().required(),
});

export const EditReportScopeForm: FC<Props> = ({ scopeId, onUpdate }) => {
    const { data, loading, error } = useReportScopeQuery({
        variables: { id: reportScopeGraphId(scopeId) },
    });
    const scope = data?.reportScope;
    const [updateScope] = useUpdateReportScopeMutation({});

    const initialValues: FormValues = useMemo(
        () => ({
            inScope: scope?.inScope ?? "",
            outOfScope: scope?.outOfScope ?? "",
        }),
        [scope],
    );

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!scope) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateScope({ variables: { input: { id: scope.id, ...values } } })
            .then(() => onUpdate?.())
            .then(() => handleSuccess(actions))
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, setFieldValue, setFieldTouched, values, isSubmitting, errors, touched, status }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StatusAlert status={status as unknown} />
                    <Form.Group className="mb-3">
                        <Form.Label>In scope</Form.Label>
                        <RichTextEditor
                            value={values.inScope ?? ""}
                            onChange={(value) => setFieldValue("inScope", value)}
                            onBlur={() => setFieldTouched("inScope")}
                            isInvalid={touched.inScope && !!errors.inScope}
                            singleMode
                        />
                        <Form.Control.Feedback type="invalid">{errors.inScope}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Out of scope</Form.Label>
                        <RichTextEditor
                            value={values.outOfScope ?? ""}
                            onChange={(value) => setFieldValue("outOfScope", value)}
                            onBlur={() => setFieldTouched("outOfScope")}
                            isInvalid={touched.outOfScope && !!errors.outOfScope}
                            singleMode
                        />
                        <Form.Control.Feedback type="invalid">{errors.outOfScope}</Form.Control.Feedback>
                    </Form.Group>
                    <Button type="submit" disabled={isSubmitting}>
                        Update
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
